import { GetAdminTripsResponse } from '@orbit/api-types'
import { useAdminFilter } from '@pages/Admin/AdminFilterContext'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

interface UseAdminTripsParams {
	page: number
	pageSize: number
	q?: string
}

/**
 * Fetches a page of the admin Trips list (GET /v1/admin/trips), scoped to the
 * global creation-date range from {@link useAdminFilter} plus the caller's
 * pagination and search term. All of these are part of the query key, so changing
 * any of them refetches. The endpoint is gated server-side by the AdminGuard;
 * non-admins receive 403.
 *
 * Previous page data is kept while the next page loads, so the table doesn't blank
 * out on pagination or search.
 */
export const useAdminTrips = ({ page, pageSize, q }: UseAdminTripsParams) => {
	const { from, to, includeInternal } = useAdminFilter()

	return useQuery<GetAdminTripsResponse>({
		queryKey: queryKeys.adminTrips(from, to, page, pageSize, q, includeInternal),
		queryFn: async () => {
			const response = await api.get<GetAdminTripsResponse>('/admin/trips', {
				params: { from, to, page, pageSize, q: q || undefined, includeInternal: includeInternal || undefined },
			})
			return response.data
		},
		placeholderData: keepPreviousData,
	})
}
